import {match} from 'ts-pattern';

import {prop, range} from 'ramda';
import {isNonEmptyArray} from 'ramda-adjunct';

import {DASHBOARD_TILE_SIZES} from '../consts/dashboardTileSizes';
import {DashboardGridRect} from '../types/DashboardGridRect';
import {DashboardTileCoordinates} from '../types/DashboardTileCoordinates';
import {DashboardTileId} from '../types/DashboardTileId';
import {
  compareDashboardGridPositions,
  DASHBOARD_GRID_COLUMNS,
  dashboardGridTilesOverlap,
  isDashboardGridPositionValid,
  PositionedDashboardGridTile,
} from './allocateDashboardGrid';
import {closeEmptyDashboardRows} from './closeEmptyDashboardRows';
import {getDashboardGapSlots} from './getDashboardGapSlots';
import {getDashboardStructuralBand} from './getDashboardStructuralBand';
import {isDashboardTileFullWidth} from './isDashboardTileFullWidth';
import {TileDropPlacement} from './resolveTileDropPlacement';

const FIRST_COLUMN = 0;
const FIRST_ROW = 0;
const MIN_AMBIGUOUS_OCCUPANCY = 2;

export type DashboardDropOperation =
  | {
      kind: 'exchange-regions';
      source: DashboardGridRect;
      target: DashboardGridRect;
    }
  | {
      kind: 'move-into-gap';
      source: DashboardGridRect;
      target: DashboardGridRect;
    }
  | {
      kind: 'move-full-width-band';
      sourceRow: number;
      rows: number;
      destinationRow: number;
      previewRow: number;
    };

interface ProjectedDashboardDrop {
  board: PositionedDashboardGridTile[];
  operation: DashboardDropOperation;
}

export interface DashboardDropTransaction {
  baseLayoutSignature: string;
  operation: DashboardDropOperation;
  // The board the drop saves, with empty rows closed.
  board: PositionedDashboardGridTile[];
  // The board the drag preview draws over the current one: the move itself, before empty rows close,
  // so the ghosts sit where the pointer is.
  previewBoard: PositionedDashboardGridTile[];
  landing: PositionedDashboardGridTile;
  landingIndex: number;
  changedIds: DashboardTileId[];
}

const getTileRect = (tile: PositionedDashboardGridTile): DashboardGridRect => ({
  column: tile.column,
  row: tile.row,
  columns: prop(tile.size, DASHBOARD_TILE_SIZES).columns,
  rows: prop(tile.size, DASHBOARD_TILE_SIZES).rows,
});

const getRectBottom = (rect: DashboardGridRect) => rect.row + rect.rows;
const getRectRight = (rect: DashboardGridRect) => rect.column + rect.columns;

const rectsOverlap = (first: DashboardGridRect, second: DashboardGridRect) =>
  first.column < getRectRight(second) &&
  getRectRight(first) > second.column &&
  first.row < getRectBottom(second) &&
  getRectBottom(first) > second.row;

const rectContains = (outer: DashboardGridRect, inner: DashboardGridRect) =>
  inner.column >= outer.column &&
  inner.row >= outer.row &&
  getRectRight(inner) <= getRectRight(outer) &&
  getRectBottom(inner) <= getRectBottom(outer);

const isBoardValid = (tiles: PositionedDashboardGridTile[]) => {
  const ids = new Set<DashboardTileId>();

  return tiles.every((tile, index) => {
    if (ids.has(tile.id) || !isDashboardGridPositionValid(tile)) {
      return false;
    }

    ids.add(tile.id);

    return !tiles.slice(index + 1).some((candidate) => dashboardGridTilesOverlap(tile, candidate));
  });
};

const sortBoard = (tiles: PositionedDashboardGridTile[]) =>
  [...tiles].sort((first, second) => compareDashboardGridPositions(first, second));

export const getDashboardLayoutSignature = (tiles: PositionedDashboardGridTile[]) =>
  JSON.stringify(sortBoard(tiles).map((tile) => [tile.id, tile.size, tile.column, tile.row]));

const translateTile = (
  tile: PositionedDashboardGridTile,
  from: DashboardGridRect,
  to: DashboardGridRect
): PositionedDashboardGridTile => ({
  ...tile,
  column: tile.column + to.column - from.column,
  row: tile.row + to.row - from.row,
});

const exchangeRegions = (
  board: PositionedDashboardGridTile[],
  source: DashboardGridRect,
  target: DashboardGridRect
): PositionedDashboardGridTile[] | null => {
  if (
    source.columns !== target.columns ||
    source.rows !== target.rows ||
    rectsOverlap(source, target)
  ) {
    return null;
  }

  for (const tile of board) {
    const tileRect = getTileRect(tile);
    const intersectsSource = rectsOverlap(tileRect, source);
    const intersectsTarget = rectsOverlap(tileRect, target);

    if (
      (intersectsSource && !rectContains(source, tileRect)) ||
      (intersectsTarget && !rectContains(target, tileRect))
    ) {
      return null;
    }
  }

  const exchanged = board.map((tile) => {
    const tileRect = getTileRect(tile);

    if (rectContains(source, tileRect)) {
      return translateTile(tile, source, target);
    }

    return rectContains(target, tileRect) ? translateTile(tile, target, source) : tile;
  });

  return isBoardValid(exchanged) ? sortBoard(exchanged) : null;
};

interface RankedCandidate {
  candidate: DashboardGridRect;
  occupiedTiles: number;
}

const rectsHaveSameShape = (first: DashboardGridRect, second: DashboardGridRect) =>
  first.columns === second.columns && first.rows === second.rows;

const getDescendingRange = (from: number, to: number) =>
  range(0, from - to + 1).map((offset) => from - offset);

// Ties of two or more contained tiles are ambiguous: the user could mean either region.
const pickBestCandidate = (candidates: RankedCandidate[]) => {
  const [firstCandidate, secondCandidate] = [...candidates].sort(
    (first, second) => second.occupiedTiles - first.occupiedTiles
  );

  const isAmbiguous =
    firstCandidate !== undefined &&
    secondCandidate !== undefined &&
    firstCandidate.occupiedTiles >= MIN_AMBIGUOUS_OCCUPANCY &&
    firstCandidate.occupiedTiles === secondCandidate.occupiedTiles;

  return isAmbiguous ? null : (firstCandidate?.candidate ?? null);
};

// The active tile's shape, aligned to its own width, laid over the target so it covers it whole.
const findContainingTargetRect = (
  board: PositionedDashboardGridTile[],
  source: DashboardGridRect,
  over: PositionedDashboardGridTile
): DashboardGridRect | null => {
  const overRect = getTileRect(over);
  const isValidCandidate = (candidate: DashboardGridRect) =>
    !rectsOverlap(source, candidate) &&
    rectContains(candidate, overRect) &&
    board.every((tile) => {
      const tileRect = getTileRect(tile);

      return !rectsOverlap(tileRect, candidate) || rectContains(candidate, tileRect);
    });
  const candidates = getDescendingRange(
    overRect.row,
    Math.max(FIRST_ROW, overRect.row - (source.rows - overRect.rows))
  )
    .flatMap((row) =>
      getDescendingRange(
        overRect.column,
        Math.max(FIRST_COLUMN, overRect.column - (source.columns - overRect.columns))
      )
        .filter(
          (column) =>
            column % source.columns === 0 && column + source.columns <= DASHBOARD_GRID_COLUMNS
        )
        .map((column) => ({column, row, columns: source.columns, rows: source.rows}))
    )
    .filter(isValidCandidate)
    .map((candidate) => ({
      candidate,
      occupiedTiles: board.filter((tile) => rectContains(candidate, getTileRect(tile))).length,
    }));

  // A structural band exactly as tall as the active tile is the region the user sees as one row.
  const structuralBand = getDashboardStructuralBand(board, over.id);
  const structuralCandidates =
    structuralBand !== null && structuralBand.endRow - structuralBand.startRow === source.rows
      ? candidates.filter(({candidate}) => candidate.row === structuralBand.startRow)
      : [];

  return pickBestCandidate(
    isNonEmptyArray(structuralCandidates) ? structuralCandidates : candidates
  );
};

const getExchangeTargetRect = (
  board: PositionedDashboardGridTile[],
  source: DashboardGridRect,
  over: PositionedDashboardGridTile
): DashboardGridRect | null => {
  const overRect = getTileRect(over);

  return match(overRect)
    .when(
      (target) => rectsHaveSameShape(target, source),
      (target) => target
    )
    .when(
      (target) => target.columns > source.columns || target.rows > source.rows,
      () => null
    )
    .otherwise(() => findContainingTargetRect(board, source, over));
};

const projectRegionExchange = (
  board: PositionedDashboardGridTile[],
  active: PositionedDashboardGridTile,
  over: PositionedDashboardGridTile
): ProjectedDashboardDrop | null => {
  const source = getTileRect(active);
  const target = getExchangeTargetRect(board, source, over);

  if (target === null) {
    return null;
  }

  const exchanged = exchangeRegions(board, source, target);
  const operation: DashboardDropOperation = {kind: 'exchange-regions', source, target};

  return exchanged === null ? null : {board: exchanged, operation};
};

const closeWideSourceBand = (
  board: PositionedDashboardGridTile[],
  active: PositionedDashboardGridTile,
  rows: number
) => {
  const sourceBottom = active.row + rows;

  return board
    .filter((tile) => tile.id !== active.id)
    .map((tile) => (tile.row >= sourceBottom ? {...tile, row: tile.row - rows} : tile));
};

const projectWideBandMove = (
  board: PositionedDashboardGridTile[],
  active: PositionedDashboardGridTile,
  over: PositionedDashboardGridTile,
  placement: TileDropPlacement
): ProjectedDashboardDrop | null => {
  if (placement === 'swap') {
    return null;
  }

  const rows = prop(active.size, DASHBOARD_TILE_SIZES).rows;
  const closedBoard = closeWideSourceBand(board, active, rows);
  const band = getDashboardStructuralBand(closedBoard, over.id);

  if (band === null) {
    return null;
  }

  const destinationRow = placement === 'before' ? band.startRow : band.endRow;

  if (destinationRow === active.row) {
    return null;
  }

  const shifted = closedBoard.map((tile) =>
    tile.row >= destinationRow ? {...tile, row: tile.row + rows} : tile
  );
  const movedActive = {...active, column: FIRST_COLUMN, row: destinationRow};
  const movedBoard = sortBoard([...shifted, movedActive]);

  if (!isBoardValid(movedBoard)) {
    return null;
  }

  return {
    board: movedBoard,
    operation: {
      kind: 'move-full-width-band',
      sourceRow: active.row,
      rows,
      destinationRow,
      previewRow: destinationRow > active.row ? destinationRow + rows : destinationRow,
    },
  };
};

const toDropTransaction = (
  tiles: PositionedDashboardGridTile[],
  activeId: DashboardTileId,
  projected: ProjectedDashboardDrop
): DashboardDropTransaction | null => {
  // Closing empty rows lifts whole blocks, so the reading order and the landing index stay the same.
  const board = closeEmptyDashboardRows(projected.board);
  const landingIndex = projected.board.findIndex((tile) => tile.id === activeId);
  const landing = projected.board[landingIndex];

  if (landing === undefined) {
    return null;
  }

  const changedIds = board
    .filter((tile) => {
      const previous = tiles.find((candidate) => candidate.id === tile.id);

      return (
        previous !== undefined && (previous.column !== tile.column || previous.row !== tile.row)
      );
    })
    .map((tile) => tile.id);

  if (changedIds.length === 0) {
    return null;
  }

  return {
    baseLayoutSignature: getDashboardLayoutSignature(tiles),
    operation: projected.operation,
    board,
    previewBoard: projected.board,
    landing,
    landingIndex,
    changedIds,
  };
};

export function projectDashboardDrop(
  tiles: PositionedDashboardGridTile[],
  activeId: DashboardTileId,
  overId: DashboardTileId,
  placement: TileDropPlacement
): DashboardDropTransaction | null {
  if (activeId === overId || !isBoardValid(tiles)) {
    return null;
  }

  const active = tiles.find((tile) => tile.id === activeId);
  const over = tiles.find((tile) => tile.id === overId);

  if (active === undefined || over === undefined) {
    return null;
  }

  const projected = isDashboardTileFullWidth(active.size)
    ? projectWideBandMove(tiles, active, over, placement)
    : projectRegionExchange(tiles, active, over);

  return projected === null ? null : toDropTransaction(tiles, activeId, projected);
}

// Moves only the active tile into a free place of its own shape; every other tile stays put.
export function projectDashboardGapDrop(
  tiles: PositionedDashboardGridTile[],
  activeId: DashboardTileId,
  target: DashboardTileCoordinates
): DashboardDropTransaction | null {
  if (!isBoardValid(tiles)) {
    return null;
  }

  const active = tiles.find((tile) => tile.id === activeId);
  const targetRect = getDashboardGapSlots(tiles, activeId).find(
    (slot) => slot.column === target.column && slot.row === target.row
  );

  if (active === undefined || targetRect === undefined) {
    return null;
  }

  const board = sortBoard(
    tiles.map((tile) =>
      tile.id === activeId ? {...tile, column: targetRect.column, row: targetRect.row} : tile
    )
  );

  if (!isBoardValid(board)) {
    return null;
  }

  return toDropTransaction(tiles, activeId, {
    board,
    operation: {kind: 'move-into-gap', source: getTileRect(active), target: targetRect},
  });
}
